const BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const request = async (method, path, data) => {
  const opts = { method, credentials: 'include', headers: { 'Content-Type': 'application/json' } };
  if (data !== undefined) opts.body = JSON.stringify(data);
  const res = await fetch(`${BASE}${path}`, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Erreur serveur' }));
    const detail = err.detail;
    const message = typeof detail === 'string' ? detail : (detail?.message || 'Erreur');
    const error = new Error(message);
    error.status = res.status;
    error.conflicts = (typeof detail === 'object' && detail?.conflicts) ? detail.conflicts : null;
    throw error;
  }
  return res.json();
};

// v2.12 — le site en ligne est loin (≥ 0,26 s par demande) : les grandes listes communes à plusieurs pages
// restent en mémoire 1 minute, et deux demandes identiques en même temps ne partent qu'une fois.
// Toute modification (POST, PUT, PATCH, DELETE) vide la mémoire : on revoit toujours ses propres changements.
// ponytail: mémoire globale ; les changements faits par l'assistante apparaissent au plus tard après 3 minutes.
const SHARED = new Set(['/students', '/group-courses', '/locations', '/pricing-matrix', '/finance']);
// v2.18 : le Planning d'un mois, Google et le tableau des revenus aussi (préchargés avant le clic)
const SHARED_PREFIX = ['/planning/data?', '/gtasks/events?', '/gcal/events?', '/gcal/calendars', '/group-courses/revenue-summary', '/payments?group_course_id='];
const kept = (path) => SHARED.has(path) || SHARED_PREFIX.some(p => path.startsWith(p))
  || /^\/group-courses\/[\w-]+$/.test(path);  // page d'un cours
const TTL_MS = 180_000;  // v2.17 : 3 min (Chrome ralentit le rafraîchissement des onglets en arrière-plan)
const cache = new Map();     // chemin → { at, data }
const copy = (d) => JSON.parse(JSON.stringify(d));  // chaque page a sa copie : trier une liste n'abîme pas la mémoire
const inflight = new Map();  // chemin → promesse en cours

export const clearApiCache = () => cache.clear();

// v2.13 — préchargement : les grandes listes arrivent en arrière-plan avant qu'on clique (et restent fraîches).
export const prefetchShared = (paths = [...SHARED]) => Promise.all(paths.map(path =>
  request('GET', path).then(data => cache.set(path, { at: Date.now(), data: copy(data) })).catch(() => {})));

const get = (path) => {
  const hit = cache.get(path);
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(copy(hit.data));
  if (inflight.has(path)) return inflight.get(path).then(copy);
  const p = request('GET', path)
    .then((data) => { if (kept(path)) cache.set(path, { at: Date.now(), data: copy(data) }); return data; })
    .finally(() => inflight.delete(path));
  inflight.set(path, p);
  return p.then(copy);
};

// v2.22 : après une modification, la mémoire est vidée puis rechargée en arrière-plan (sinon le prochain
// clic sur Planning/Collectifs repartait de zéro). Plusieurs modifications rapprochées → un seul rechargement.
let afterWrite = null;
let afterWriteTimer = null;
export const setAfterWrite = (fn) => { afterWrite = fn; };
const rewarm = () => {
  clearTimeout(afterWriteTimer);
  afterWriteTimer = setTimeout(() => { if (afterWrite) afterWrite(); }, 1200);
};
const write = (method) => (path, data) => {
  cache.clear();
  return request(method, path, data).finally(() => { cache.clear(); rewarm(); });
};

export const api = {
  get,
  post: write('POST'),
  put: write('PUT'),
  patch: write('PATCH'),
  delete: (path) => { cache.clear(); return request('DELETE', path); },
};
